import { Link, useSearchParams } from 'react-router-dom';
import { AuroraField } from './components/AuroraField';
import { DashboardPreview } from './components/DashboardPreview';
import { CinematicLabStory } from './components/CinematicLabStory';
import { HeroStage } from './components/HeroStage';
import { Icon } from './components/Icon';
import { MediaFigure } from './components/MediaFigure';
import { QuoteRequestForm } from './components/QuoteRequestForm';
import { ProductTour } from './components/ProductTour';
import { TeamFlow } from './components/TeamFlow';
import { ToothOrbit } from './components/ToothOrbit';
import { copy, type Language, type Plan } from './content';
import { usePointerDepth } from './motion/usePointerDepth';
import { salesEmail, supportEmail } from './services/salesLead';
import { whatsAppLink } from './services/whatsapp';

const LAB_RENDER = '/media/lab-render-1080.webp';
const LAB_RENDER_SMALL = '/media/lab-render-640.webp';
// Source is only 510px wide, so this one is confined to a narrow column until a larger
// export lands — see the note in apps/marketing-website/photo.
const TECHNICIAN = '/media/technician-510.webp';
const TECHNICIAN_SMALL = '/media/technician-360.webp';

const FEATURE_IMAGES = [
  '/media/workflow-intake-v2.jpg',
  '/media/feature-finance-v2.jpg',
  '/media/feature-inventory-v2.jpg',
  '/media/workflow-planning-v2.jpg',
  '/media/feature-reports-v2.jpg',
  '/media/feature-mobile-v2.jpg',
];

const FEATURE_ALTS = {
  ar: [
    'تسجيل وإدارة حالات التركيبات داخل مختبر الأسنان',
    'مدير المختبر يراجع الفواتير والأداء المالي',
    'فني يتابع مخزون مواد مختبر الأسنان بدقة',
    'فريق المختبر يخطط المهام والصلاحيات',
    'إدارة المختبر تراجع تقارير الإنتاج والأداء',
    'مدير المختبر يتابع العمل من الهاتف واللوحي',
  ],
  en: [
    'Registering and managing restoration cases in the dental laboratory',
    'A lab manager reviewing invoices and financial performance',
    'A technician accurately tracking dental laboratory inventory',
    'The laboratory team planning tasks and permissions',
    'Laboratory leadership reviewing production and performance reports',
    'A lab owner monitoring operations from mobile and tablet devices',
  ],
} as const;

type PageProps = { language: Language };

function SectionHeading({ eyebrow, title, description, align = 'center' }: {
  eyebrow: string; title: string; description: string; align?: 'center' | 'start';
}) {
  return (
    <div className={`section-heading section-heading--${align}`}>
      <span className="eyebrow"><Icon name="spark" />{eyebrow}</span>
      <h2>{title}</h2><p>{description}</p>
    </div>
  );
}

function QuoteLink({ language, planId = 'professional', className = 'button button--primary' }: {
  language: Language; planId?: Plan['id']; className?: string;
}) {
  return <Link className={className} to={`/contact?plan=${planId}`}>{copy[language].headerCta}<Icon name="arrow" /></Link>;
}

function PageHero({ language, eyebrow, title, accent, description, icon, image, imageAlt }: {
  language: Language; eyebrow: string; title: string; accent?: string; description: string; icon: string;
  image?: string; imageAlt?: string;
}) {
  return (
    <section className={`page-hero ${image ? 'page-hero--visual' : ''}`}>
      <AuroraField intensity={0.7} />
      <div className="page-hero__grid" aria-hidden="true" />
      <div className="container page-hero__layout">
        <div className="page-hero__inner reveal is-visible">
          <span className="page-hero__icon"><Icon name={icon} /></span>
          <span className="eyebrow"><Icon name="spark" />{eyebrow}</span>
          <h1>{title} {accent && <span>{accent}</span>}</h1>
          <p>{description}</p>
          <div className="page-hero__actions">
            <QuoteLink language={language} className="button button--primary button--large" />
            <Link className="button button--ghost button--large" to="/pricing">
              {language === 'ar' ? 'استعرض الخطط' : 'View plans'}<Icon name="arrow" />
            </Link>
          </div>
        </div>
        {image && (
          <figure className="page-hero__media reveal is-visible">
            <img src={image} width={1536} height={1024} alt={imageAlt || ''} decoding="async" />
            <figcaption><Icon name={icon} /><span>{eyebrow}</span><i aria-hidden="true" /></figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}

function FeatureGrid({ language, limit }: PageProps & { limit?: number }) {
  const features = copy[language].features.items.slice(0, limit);
  return (
    <div className="feature-grid">
      {features.map((feature, index) => (
        <article className="feature-card reveal" data-tilt key={feature.title}>
          <figure className="feature-card__media">
            <img src={FEATURE_IMAGES[index]} width={1536} height={1024} alt={FEATURE_ALTS[language][index]} loading="lazy" decoding="async" />
            <span className="feature-card__icon"><Icon name={feature.icon} /></span>
            <span className="feature-card__number">0{index + 1}</span>
          </figure>
          <div className="feature-card__body">
            <h3>{feature.title}</h3><p>{feature.description}</p>
            <ul>{feature.points.map((point) => <li key={point}><Icon name="check" />{point}</li>)}</ul>
          </div>
        </article>
      ))}
    </div>
  );
}

function WorkflowGrid({ language }: PageProps) {
  const text = copy[language].workflow;
  const workflowImages = [
    '/media/workflow-intake-v2.jpg',
    '/media/workflow-planning-v2.jpg',
    '/media/workflow-production-v2.jpg',
    '/media/workflow-delivery-v2.jpg',
  ];
  const workflowAlts = language === 'ar'
    ? [
        'تسجيل حالة جديدة واستلام نموذج الأسنان داخل المختبر',
        'مشرف المختبر يخطط العمل ويسند الحالات إلى الفنيين',
        'فني أسنان ينفذ التركيبة ويفحص جودتها تحت المجهر',
        'تسليم التركيبة بأمان مع تأكيد الفاتورة والتحصيل',
      ]
    : [
        'Registering a new case and receiving its dental model in the laboratory',
        'A laboratory supervisor planning work and assigning cases to technicians',
        'A dental technician producing and inspecting a restoration under a microscope',
        'Secure restoration delivery with invoice and payment confirmation',
      ];
  return (
    <div className="workflow-grid">
      {text.steps.map((step, index) => (
        <article className="workflow-card reveal" data-tilt key={step.number}>
          <figure className="workflow-card__media">
            <img
              src={workflowImages[index]}
              width={1536}
              height={1024}
              alt={workflowAlts[index]}
              loading="lazy"
              decoding="async"
              style={{ animationDelay: `${index * -3.5}s` }}
            />
            <span className="workflow-card__icon"><Icon name={['invoice', 'team', 'tooth', 'payment'][index]} /></span>
            <b className="workflow-card__number">{step.number}</b>
          </figure>
          <div className="workflow-card__body">
            <h3>{step.title}</h3><p>{step.description}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function PricingGrid({ language }: PageProps) {
  const text = copy[language].pricing;
  const planImages = ['/media/feature-mobile-v2.jpg', '/media/feature-reports-v2.jpg', '/media/feature-support-v2.jpg'];
  return (
    <div className="pricing-grid">
      {text.plans.map((plan, index) => (
        <article className={`pricing-card reveal ${plan.featured ? 'pricing-card--featured' : ''}`} data-tilt key={plan.id}>
          <figure className="pricing-card__media"><img src={planImages[index]} width={1536} height={1024} alt="" loading="lazy" decoding="async" /></figure>
          {plan.featured && <span className="pricing-card__badge"><Icon name="spark" />{text.featuredLabel}</span>}
          <div className="pricing-card__head"><span>{plan.audience}</span><h3>{plan.name}</h3><p>{plan.description}</p></div>
          <div className="pricing-card__price">
            {plan.price ? (
              <>
                <strong>{plan.price.monthly.toLocaleString('en-US')} <span>{text.currency}</span></strong>
                <em>/ {text.perMonth}</em>
                <small>{plan.price.yearly.toLocaleString('en-US')} {text.currency} {language === 'ar' ? 'سنويًا' : 'yearly'} · {text.annualNote}</small>
              </>
            ) : (
              <><strong>{text.quote}</strong><small>{text.quoteNote}</small></>
            )}
          </div>
          <ul>{plan.features.map((feature) => <li key={feature}><span><Icon name="check" /></span>{feature}</li>)}</ul>
          <Link className={`button button--wide ${plan.featured ? 'button--primary' : 'button--ghost'}`} to={`/contact?plan=${plan.id}`}>
            {text.cta}<Icon name="arrow" />
          </Link>
        </article>
      ))}
    </div>
  );
}

function PurchaseJourney({ language }: PageProps) {
  const text = copy[language].purchase;
  return (
    <div className="purchase-journey reveal">
      <div className="purchase-journey__intro"><h3>{text.title}</h3><p>{text.description}</p></div>
      <div className="purchase-journey__steps">
        {text.steps.map((step, index) => (
          <article key={step.title}>
            <span><Icon name={step.icon} /><i>{index + 1}</i></span>
            <div><strong>{step.title}</strong><small>{step.description}</small></div>
            {index < text.steps.length - 1 && <Icon className="journey-arrow" name="arrow" />}
          </article>
        ))}
      </div>
    </div>
  );
}

function SecurityPanel({ language }: PageProps) {
  const text = copy[language].security;
  return (
    <div className="security__panel reveal">
      <div className="security__copy">
        <SectionHeading align="start" eyebrow={text.eyebrow} title={text.title} description={text.description} />
        <div className="security-list">
          {text.points.map((point) => (
            <article key={point.title}><span><Icon name={point.icon} /></span><div><h3>{point.title}</h3><p>{point.description}</p></div></article>
          ))}
        </div>
      </div>
      <div className="security-visual">
        <img className="security-visual__photo" src="/media/feature-security-v2.jpg" width={1536} height={1024} alt={language === 'ar' ? 'مسؤول الأمان يتحقق من الوصول المحمي لأنظمة مختبر الأسنان' : 'A security specialist verifies protected access to dental lab systems'} loading="lazy" decoding="async" />
        <div className="security-visual__badge"><span><Icon name="shield" /></span><div><strong>{language === 'ar' ? 'وصول محمي' : 'Protected access'}</strong><small>{language === 'ar' ? 'صلاحيات · نسخ احتياطي · تتبع' : 'Permissions · backup · audit'}</small></div><i><Icon name="check" /></i></div>
      </div>
    </div>
  );
}

function FaqList({ language, limit }: PageProps & { limit?: number }) {
  const items = copy[language].faq.items.slice(0, limit);
  return (
    <div className="faq__items reveal">
      {items.map((item, index) => (
        <details key={item.question} open={index === 0}>
          <summary><span>{item.question}</span><i><Icon name="plus" /></i></summary><p>{item.answer}</p>
        </details>
      ))}
    </div>
  );
}

function ToothSection({ language }: PageProps) {
  const text = copy[language].chart;
  return (
    <section className="tooth-section section">
      <div className="container tooth-section__grid">
        <div className="reveal">
          <SectionHeading align="start" eyebrow={text.eyebrow} title={text.title} description={text.description} />
          <div className="tooth-section__points">
            {text.points.map((point) => (
              <article key={point.title}>
                <span><Icon name={point.icon} /></span>
                <div><h3>{point.title}</h3><p>{point.description}</p></div>
              </article>
            ))}
          </div>
        </div>
        <ToothOrbit language={language} />
      </div>
    </section>
  );
}

function TeamSection({ language }: PageProps) {
  const text = copy[language].team;
  return (
    <section className="team-section section section--compact">
      <div className="container">
        <SectionHeading eyebrow={text.eyebrow} title={text.title} description={text.description} />
        <div className="team-section__stage">
          <MediaFigure
            src="/media/workflow-planning-v2.jpg"
            height={390}
            alt={language === 'ar' ? 'فريق مختبر الأسنان يخطط الحالات ويوزع المسؤوليات' : 'A dental laboratory team planning cases and assigning ownership'}
            title={language === 'ar' ? 'الفريق يرى نفس الصورة' : 'One shared picture for the team'}
            caption={language === 'ar' ? 'مهام واضحة، مسؤوليات محددة، وتحديثات تصل للجميع.' : 'Clear tasks, defined ownership and updates everyone can follow.'}
          />
          <div className="team-section__flow reveal"><TeamFlow language={language} /></div>
        </div>
      </div>
    </section>
  );
}

function TechnicianSplit({ language }: PageProps) {
  const ar = language === 'ar';
  return (
    <section className="section section--compact">
      <div className="container image-split">
        <MediaFigure
          src={TECHNICIAN}
          srcSmall={TECHNICIAN_SMALL}
          height={300}
          alt={ar ? 'فني أسنان يعمل على ماسح رقمي وشاشة تصميم داخل المعمل' : 'A dental technician working with a digital scanner and design screen'}
          title={ar ? 'الفني يرى ما يحتاجه فقط' : 'Technicians see exactly what they need'}
          caption={ar ? 'الحالة والمواصفات والموعد في شاشة واحدة.' : 'Case, specification and due date on one screen.'}
        />
        <div className="image-split__copy reveal">
          <h2>{ar ? 'العمل يصل للفني جاهزًا' : 'Work reaches the bench ready to start'}</h2>
          <p>
            {ar
              ? 'لا مكالمات للتأكيد ولا ورق يضيع بين المكاتب. الفني يفتح الحالة فيجد المواصفات والمرفقات والموعد، ويحدّث الحالة فور انتهائه فيراها الجميع في نفس اللحظة.'
              : 'No confirmation calls and no paper lost between desks. A technician opens the case to find its specification, attachments and due date, and the status they set is visible to everyone the moment they set it.'}
          </p>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ language, title, description }: PageProps & { title?: string; description?: string }) {
  const text = copy[language].finalCta;
  return (
    <section className="final-cta section section--compact">
      <div className="container final-cta__panel reveal">
        <div className="final-cta__rings" aria-hidden="true"><i /><i /><i /></div>
        <span className="eyebrow"><Icon name="spark" />{text.eyebrow}</span>
        <h2>{title || text.title}</h2><p>{description || text.description}</p>
        <div className="final-cta__actions">
          <QuoteLink language={language} className="button button--primary button--large" />
          <a className="button button--ghost button--large" href={`mailto:${salesEmail}`}><Icon name="mail" />{text.secondary}</a>
        </div>
      </div>
    </section>
  );
}

export function HomePage({ language }: PageProps) {
  const text = copy[language];
  const heroRef = usePointerDepth<HTMLElement>();
  return (
    <>
      <section className="hero section" ref={heroRef}>
        <AuroraField />
        <div className="container hero__grid">
          <div className="hero__copy reveal is-visible">
            <span className="hero__eyebrow"><i /><span>{text.hero.eyebrow}</span></span>
            <h1>{text.hero.titleBefore} <span>{text.hero.titleAccent}</span></h1>
            <p>{text.hero.description}</p>
            <div className="hero__actions">
              <QuoteLink language={language} className="button button--primary button--large" />
              <Link className="button button--ghost button--large" to="/features"><Icon name="play" />{text.hero.secondaryCta}</Link>
            </div>
            <div className="hero__note"><Icon name="shield" /><span>{text.hero.note}</span></div>
          </div>
          <div className="hero__visual reveal is-visible">
            <div className="hero__visual-glow" aria-hidden="true" />
            <HeroStage language={language} />
          </div>
        </div>
      </section>
      <section className="trust-strip"><div className="container trust-strip__grid">{text.trust.map((item, index) => <article className="reveal" key={item.value}><span className="trust-strip__icon"><Icon name={['server', 'shield', 'backup', 'spark'][index]} /></span><div><strong>{item.value}</strong><small>{item.label}</small></div></article>)}</div></section>
      <CinematicLabStory language={language} />
      <ProductTour language={language} />
      <section className="features section"><div className="container"><SectionHeading eyebrow={text.features.eyebrow} title={text.features.title} description={text.features.description} /><FeatureGrid language={language} limit={3} /><div className="section-link"><Link className="button button--ghost" to="/features">{language === 'ar' ? 'كل مميزات النظام' : 'All platform features'}<Icon name="arrow" /></Link></div></div></section>
      <ToothSection language={language} />
      <TeamSection language={language} />
      <FinalCta language={language} />
    </>
  );
}

export function FeaturesPage({ language }: PageProps) {
  const text = copy[language].features;
  return <><PageHero language={language} eyebrow={text.eyebrow} title={language === 'ar' ? 'كل أدوات المختبر' : 'Every lab tool'} accent={language === 'ar' ? 'في نظام واحد' : 'in one platform'} description={text.description} icon="spark" image="/media/feature-reports-v2.jpg" imageAlt={language === 'ar' ? 'إدارة المختبر تراجع الأداء من منصة موحدة' : 'Lab leadership reviewing performance from one platform'} /><section className="features section"><div className="container"><FeatureGrid language={language} /></div></section><section className="section section--compact"><div className="container"><MediaFigure
    src={LAB_RENDER}
    srcSmall={LAB_RENDER_SMALL}
    focus="80% 32%"
    height={380}
    alt={language === 'ar' ? 'شاشة عرض تصميم التركيبات داخل معمل الأسنان' : 'Restoration design displayed on a dental laboratory screen'}
    title={language === 'ar' ? 'صورة واحدة لكل حالة' : 'One picture of every case'}
    caption={language === 'ar' ? 'التصميم والملاحظات والمرفقات في مكان واحد مرتبط بالحالة.' : 'Design, notes and attachments stay attached to the case record.'}
  /></div></section><ToothSection language={language} /><section className="module-map section section--compact"><div className="container"><SectionHeading eyebrow={language === 'ar' ? 'مترابط من الأساس' : 'Connected by design'} title={language === 'ar' ? 'كل معلومة تصل إلى مكانها الصحيح' : 'Every detail reaches the right workflow'} description={language === 'ar' ? 'الحالة تؤثر في الإنتاج، والتسليم يحدث الحساب، والمشتريات تحدث المخزون—بدون إدخال نفس المعلومة مرتين.' : 'Cases inform production, delivery updates finance and purchasing updates inventory—without entering the same detail twice.'} /><div className="module-map__grid">{['tooth','team','invoice','inventory'].map((icon,index)=><article key={icon}><span><Icon name={icon} /></span><strong>{copy[language].showcase.tabs[index]}</strong><Icon name="arrow" /></article>)}</div></div></section><FinalCta language={language} /></>;
}

export function WorkflowPage({ language }: PageProps) {
  const text = copy[language].workflow;
  return <><PageHero language={language} eyebrow={text.eyebrow} title={language === 'ar' ? 'دورة عمل واضحة' : 'A clear workflow'} accent={language === 'ar' ? 'من البداية للنهاية' : 'from start to finish'} description={text.description} icon="reports" image="/media/workflow-planning-v2.jpg" imageAlt={language === 'ar' ? 'تخطيط دورة العمل وتوزيع حالات مختبر الأسنان' : 'Planning a dental laboratory workflow and assigning cases'} /><section className="workflow section"><div className="container"><SectionHeading eyebrow={text.eyebrow} title={text.title} description={text.description} /><WorkflowGrid language={language} /></div></section><section className="process-detail section"><div className="container process-detail__grid">{[
    {icon:'clock',ar:'مواعيد وأولويات',en:'Due dates and priority',ard:'اعرف ما يجب إنجازه اليوم وما يحتاج تدخلًا قبل التأخير.',end:'Know what is due today and what needs action before it slips.'},
    {icon:'team',ar:'مسؤولية واضحة',en:'Clear ownership',ard:'كل حالة مرتبطة بالفني والمسؤول والملاحظات المطلوبة.',end:'Every case keeps its technician, owner and production notes.'},
    {icon:'bell',ar:'تنبيهات عملية',en:'Actionable alerts',ard:'تابع التغيير والتسليم والرسائل من نفس سياق العمل.',end:'Track changes, delivery and messages in the same context.'},
  ].map(item=><article className="reveal" data-tilt key={item.icon}><span><Icon name={item.icon} /></span><h3>{language==='ar'?item.ar:item.en}</h3><p>{language==='ar'?item.ard:item.end}</p></article>)}</div></section><TeamSection language={language} /><TechnicianSplit language={language} /><FinalCta language={language} /></>;
}

export function SecurityPage({ language }: PageProps) {
  const text = copy[language].security;
  return <><PageHero language={language} eyebrow={text.eyebrow} title={language === 'ar' ? 'أمان يواكب' : 'Security built for'} accent={language === 'ar' ? 'تشغيل المختبر' : 'lab operations'} description={text.description} icon="shield" image="/media/feature-security-v2.jpg" imageAlt={language === 'ar' ? 'تحكم آمن ومهني في أنظمة مختبر الأسنان' : 'Professional secure control of dental laboratory systems'} /><section className="security section"><div className="container"><SecurityPanel language={language} /></div></section><section className="security-facts section section--compact"><div className="container security-facts__grid">{text.points.map(point=><article className="reveal" data-tilt key={point.title}><Icon name={point.icon} /><h3>{point.title}</h3><p>{point.description}</p></article>)}</div></section><section className="section section--compact"><div className="container"><MediaFigure
    src={LAB_RENDER}
    srcSmall={LAB_RENDER_SMALL}
    focus="88% 58%"
    height={340}
    alt={language === 'ar' ? 'تحكم دقيق في بيئة عمل معمل الأسنان' : 'Precise control over the dental laboratory environment'}
    title={language === 'ar' ? 'تحكم كامل في من يرى ماذا' : 'Full control over who sees what'}
    caption={language === 'ar' ? 'صلاحيات حسب الدور، جلسات موثقة، ونسخ احتياطي منتظم.' : 'Role-based permissions, audited sessions and regular backups.'}
  /></div></section><FinalCta language={language} /></>;
}

export function PricingPage({ language }: PageProps) {
  const text = copy[language].pricing;
  return <><PageHero language={language} eyebrow={text.eyebrow} title={language === 'ar' ? 'خطة تناسب' : 'A plan shaped for'} accent={language === 'ar' ? 'حجم مختبرك' : 'your lab'} description={text.description} icon="plan" image="/media/feature-support-v2.jpg" imageAlt={language === 'ar' ? 'جلسة احترافية لتجهيز النظام حسب حجم المختبر' : 'A professional onboarding session shaped around the laboratory'} /><section className="pricing section"><div className="container"><PricingGrid language={language} /><PurchaseJourney language={language} /></div></section><section className="faq section section--compact"><div className="container faq__grid"><div className="faq__intro"><SectionHeading align="start" eyebrow={copy[language].faq.eyebrow} title={copy[language].faq.title} description={copy[language].faq.description} /><Link className="text-link" to="/faq">{language==='ar'?'عرض كل الأسئلة':'View all questions'}<Icon name="arrow" /></Link></div><FaqList language={language} limit={3} /></div></section><FinalCta language={language} /></>;
}

export function FaqPage({ language }: PageProps) {
  const text = copy[language].faq;
  return <><PageHero language={language} eyebrow={text.eyebrow} title={language === 'ar' ? 'كل ما تريد معرفته' : 'Everything you need'} accent={language === 'ar' ? 'قبل البدء' : 'before you start'} description={text.description} icon="chat" image="/media/feature-support-v2.jpg" imageAlt={language === 'ar' ? 'فريق الدعم يشرح النظام لفريق مختبر الأسنان' : 'The support team guiding a dental laboratory team'} /><section className="faq section"><div className="container faq-page__grid"><FaqList language={language} /><aside className="faq-contact-card"><figure className="context-card__media"><img src="/media/feature-support-v2.jpg" width={1536} height={1024} alt="" loading="lazy" decoding="async" /></figure><span><Icon name="mail" /></span><h2>{language==='ar'?'لم تجد إجابتك؟':'Still have a question?'}</h2><p>{language==='ar'?'أرسل سؤالك وسنرد عليك بما يناسب بيئة مختبرك.':'Send your question and we will answer for your lab environment.'}</p><Link className="button button--primary" to="/contact">{language==='ar'?'تواصل معنا':'Contact us'}<Icon name="arrow" /></Link></aside></div></section></>;
}

export function ContactPage({ language }: PageProps) {
  const text = copy[language];
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get('plan');
  const planId: Plan['id'] = requestedPlan === 'essential' || requestedPlan === 'enterprise' ? requestedPlan : 'professional';
  return <><PageHero language={language} eyebrow={text.form.eyebrow} title={language === 'ar' ? 'ابدأ طلب' : 'Start your'} accent={language === 'ar' ? 'عرض السعر' : 'quote request'} description={text.form.description} icon="mail" image="/media/feature-support-v2.jpg" imageAlt={language === 'ar' ? 'جلسة تعريف ودعم لفريق مختبر الأسنان' : 'An onboarding and support session for a dental laboratory team'} /><section className="contact-page section"><div className="container contact-page__grid"><div className="contact-page__form"><QuoteRequestForm language={language} plans={text.pricing.plans} initialPlanId={planId} /></div><aside className="contact-page__aside"><figure className="context-card__media"><img src="/media/feature-support-v2.jpg" width={1536} height={1024} alt="" loading="lazy" decoding="async" /></figure><span className="eyebrow"><Icon name="shield" />{language==='ar'?'تواصل واضح وآمن':'Clear, secure contact'}</span><h2>{language==='ar'?'ماذا يحدث بعد إرسال الطلب؟':'What happens after you submit?'}</h2><ol>{[
    language==='ar'?'نراجع حجم المختبر وطريقة التشغيل الحالية.':'We review your lab size and current workflow.',
    language==='ar'?'نرتب جلسة قصيرة لتأكيد النطاق ونقل البيانات.':'We arrange a short scope and migration call.',
    language==='ar'?'يصلك عرض رسمي بالسعر والتنفيذ والدعم.':'You receive a formal price, rollout and support proposal.',
  ].map((item,index)=><li key={item}><span>{index+1}</span>{item}</li>)}</ol><div className="contact-direct"><strong>{language==='ar'?'تواصل مباشر':'Direct contact'}</strong><a className="contact-direct__wa" href={whatsAppLink(language==='ar'?'مرحبًا، أريد الاستفسار عن نظام إدارة مختبرات الأسنان.':'Hello, I would like to ask about the Dental Lab System.')} target="_blank" rel="noreferrer noopener"><Icon name="chat" />{language==='ar'?'واتساب — رد فوري':'WhatsApp — instant reply'}</a><a href={`mailto:${salesEmail}`}><Icon name="mail" />{salesEmail}</a><a href={`mailto:${supportEmail}`}><Icon name="chat" />{supportEmail}</a></div></aside></div></section></>;
}

export function LegalPage({ language, kind }: PageProps & { kind: 'privacy' | 'terms' | 'refund' }) {
  const ar = language === 'ar';
  const titles = { privacy: ar ? 'سياسة الخصوصية' : 'Privacy policy', terms: ar ? 'شروط الاستخدام' : 'Terms of use', refund: ar ? 'سياسة الاسترجاع' : 'Refund policy' };
  return <><PageHero language={language} eyebrow={ar?'المعلومات القانونية':'Legal information'} title={titles[kind]} description={ar?'نسخة تمهيدية للمراجعة قبل الإطلاق التجاري النهائي.':'A pre-launch version for review before commercial publication.'} icon="shield" /><section className="legal-page section"><article className="container"><div className="legal-notice"><Icon name="bell" /><p>{ar?'يجب إدخال اسم الكيان القانوني والعنوان والاختصاص القضائي قبل نشر هذه الصفحة للعملاء.':'The legal entity, address and jurisdiction must be inserted before this page is published to customers.'}</p></div><h2>{ar?'مبدأنا':'Our principle'}</h2><p>{ar?'نجمع الحد الأدنى من البيانات اللازمة لتقديم العرض والدعم وتشغيل الترخيص، ولا نخزن بيانات البطاقات داخل الموقع.':'We collect only the information needed to provide quotes, support and licensing. Card data is not stored by this website.'}</p><h2>{ar?'بيانات التواصل':'Contact data'}</h2><p>{ar?'تُستخدم بيانات نموذج التواصل للرد على طلبك المتعلق بالمنتج فقط، وفق المدة والالتزامات التي ستحددها النسخة القانونية النهائية.':'Contact-form details are used to respond to your product request, subject to the retention and obligations in the final legal version.'}</p><a href={`mailto:${supportEmail}`}>{supportEmail}</a></article></section></>;
}

export function NotFoundPage({ language }: PageProps) {
  return <section className="not-found"><div className="container"><strong>404</strong><h1>{language==='ar'?'الصفحة غير موجودة':'Page not found'}</h1><p>{language==='ar'?'الرابط الذي طلبته غير متاح.':'The requested URL is not available.'}</p><Link className="button button--primary" to="/">{language==='ar'?'العودة للرئيسية':'Back home'}</Link></div></section>;
}
